import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";

export interface CollectionQueueSummaryApi {
  collectionQueueCount: number;
  accountsRequiringActionTotal: number;
}

/**
 * Hook to fetch collection queue summary.
 * @param assignedToUserId — when provided (live mode only), passes `?assignedTo=<id>` so the
 *   backend returns counts scoped to that user. Pass `undefined` for the "Everyone" view.
 */
export function useCollectionQueueSummary(assignedToUserId?: string) {
  const { apiGet } = useApi();

  const store = getGlobalDataStore();
  const allCases = store.getAllCases();
  const activeCases = allCases.filter((x) => x.status === "Active");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const demoData: CollectionQueueSummaryApi = {
    collectionQueueCount: activeCases.length,
    accountsRequiringActionTotal: activeCases
      .filter((x) => new Date(x.nextActionDate) <= today)
      .reduce((sum, x) => sum + (x.baseAmount ?? 0), 0),
  };

  // Build query params — only include assignedTo when a specific user is selected
  const params: Record<string, string | number | boolean | undefined> | undefined =
    assignedToUserId ? { assignedTo: assignedToUserId } : undefined;

  return useModeAwareQuery<CollectionQueueSummaryApi, Error>(
    ["collection-queue-summary", assignedToUserId ?? "everyone"],
    async ({ signal }) => {
      return apiGet<CollectionQueueSummaryApi>(
        "/api/v1/collection-cases/queue-summary",
        params,
        signal
      );
    },
    demoData,
    { staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: false }
  );
}
